import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ChatHeader from "../components/ChatHeader";
import ChatSidebar from "../components/ChatSidebar";
import ChatFilters from "../components/ChatFilters";
import ChatMessages from "../components/ChatMessages";
import ChatInput from "../components/ChatInput";
import WelcomeScreen from "../components/WelcomeScreen";
import useChatState from "../hooks/useChatState";
import useChatAPI from "../hooks/useChatAPI";
import useChatHistory from "../hooks/useChatHistory";
import useChatFilters from "../hooks/useChatFilters";
import ConfirmDialog from '../components/ConfirmDialog';
import useConfirmDialog from '../hooks/useConfirmDialog';


const resolveStoredDisplayName = () => {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const directKeys = ["user_display_name", "user_name", "displayName"];
    for (const key of directKeys) {
      const value = window.localStorage.getItem(key);
      if (value) return value;
    }
    const msalKey = Object.keys(window.localStorage).find(
      (key) =>
        key.toLowerCase().includes("msal") &&
        key.toLowerCase().includes("account")
    );
    if (msalKey) {
      const raw = window.localStorage.getItem(msalKey);
      if (raw) {
        const account = JSON.parse(raw);
        if (account?.name) return account.name;
        if (account?.username) return account.username.split("@")[0];
      }
    }
  } catch (error) {
    console.warn("Unable to resolve display name:", error);
  }
  return null;
};

const ChatPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const { dialogState, showConfirm, hideConfirm } = useConfirmDialog();

  //const [isDeepResearch, setIsDeepResearch] = useState(false);

  // UI State
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [apiError, setApiError] = useState(null);

  // Streaming refs for real-time updates
  const streamingMessageRef = useRef(null);
  const accumulatedContentRef = useRef("");
  const currentMessageRef = useRef(null); // Add this to track the current message

  // Custom hooks
  const chatState = useChatState();
  const chatAPI = useChatAPI();
  const chatHistory = useChatHistory();
  const chatFilters = useChatFilters();

  const sampleQuestions = [

    "show me the trend of top hammer bit failures by types across the different sales areas , give me the different customers in the different sales entities as well.  give the top analysis of the failures and a summary",
    "HINDUSTAN ZINC LIMITED at Sindesar Khurd Mine T38 shank adapter failures",
   "Site -'Sunshine - Melbourne' Date of visit- 12 Jun 2025",
   "What happened at Sindesar khurd mine september 2025?"
  ];

  const storedDisplayName = useMemo(() => resolveStoredDisplayName(), []);
  const displayName =
    chatState.userProfile?.displayName ||
    chatState.userProfile?.name ||
    chatState.profile?.displayName ||
    chatState.profile?.name ||
    chatState.user?.displayName ||
    chatState.user?.name ||
    storedDisplayName ||
    "there";

  useEffect(() => {
    if (typeof window !== "undefined" && displayName && displayName !== "there") {
      window.localStorage.setItem("user_display_name", displayName);
    }
  }, [displayName]);

  const showWelcome = chatState.messages.length <= 1;


  // Add/remove body class for chat page
  useEffect(() => {
    document.body.classList.add('chat-page');
    return () => {
      document.body.classList.remove('chat-page');
    };
  }, []);

  // Initialize on mount
  useEffect(() => {
    chatState.initializeChat();
    chatHistory.loadChatHistory();
    chatFilters.loadDropdownOptions();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatState.messages]);

  // Handle responsive sidebars
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setLeftSidebarOpen(true);
        setRightSidebarOpen(false);
      } else {
        // Keep sidebar open on mobile for homepage if no messages
        if (chatState.messages.length <= 1) {
          setLeftSidebarOpen(true);
        } else {
          setLeftSidebarOpen(false);
        }
        setRightSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [chatState.messages.length]);

  const handleDeleteChat = (chatId, event) => {
    chatHistory.deleteChatSession(chatId, event, showConfirm);
  };

  // Send message handler
  const handleSendMessage = async (messageOverride = null) => {
    const messageToSend = messageOverride || inputMessage;

    // Ensure messageToSend is a string and not empty
    if (
      !messageToSend ||
      typeof messageToSend !== "string" ||
      !messageToSend.trim() ||
      chatState.isTyping
    ) {
      return;
    }

    // Store the current message for later use
    currentMessageRef.current = messageToSend.trim();

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: messageToSend.trim(),
      timestamp: new Date(),
    };

    chatState.addMessage(userMessage);

    if (!messageOverride) {
      setInputMessage("");
    }
    chatState.setIsTyping(true);

    try {
      setApiError(null);
      chatState.setConnectionStatus("sending");

      // Create bot message placeholder immediately
      const botMessageId = Date.now() + 1;
      const botMessage = {
        id: botMessageId,
        type: "bot",
        content: "",
        timestamp: new Date(),
        isStreaming: true,
        systemMessage: "Connected and analyzing...",
        sessionId: chatState.sessionId,
      };

      chatState.addMessage(botMessage);
      chatState.setConnectionStatus("receiving");

      // Reset accumulator
      accumulatedContentRef.current = "";

      // Get reference to the message DOM element for direct manipulation
      setTimeout(() => {
        const messageEl = document.querySelector(
          `[data-message-id="${botMessageId}"]`
        );
        if (messageEl) {
          streamingMessageRef.current =
            messageEl.querySelector(".streaming-content");
        }
      }, 0);

      await chatAPI.streamMessage(
        chatState.sessionId,
        messageToSend.trim(),
        chatFilters.filters,
        (data) => handleStreamData(data, botMessageId)
      );
    } catch (error) {
      console.error("API Error:", error);
      setApiError(error.message);
      chatState.setConnectionStatus("error");
      chatState.setIsTyping(false);

      const errorMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: `I apologize, but I encountered an error: ${error.message}. Please check your connection and try again.`,
        timestamp: new Date(),
        isError: true,
      };
      chatState.addMessage(errorMessage);
    }
  };


  // Handle streaming data - FIXED to match your original API format
  const handleStreamData = (data, botMessageId) => {
    if (!data || typeof data !== "object") return;


    switch (data.type) {
      case "connection":
      case "system":
      case "sql_generated":
        // Update system message for the specific bot message
        chatState.setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? { ...msg, systemMessage: data.message || data.content }
              : msg
          )
        );
        break;

      case "thinking":
        chatState.setIsTyping(true);
        chatState.setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? { ...msg, systemMessage: "Generating response..." }
              : msg
          )
        );
        break;

      case "response_metadata":
        chatState.setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? {
                  ...msg,
                  responseType: data.response_type,
                  chartConfig: data.chart_config,
                  systemMessage: "Preparing response...",
                }
              : msg
          )
        );
        break;

      case "token":
      case "content_stream":

      if (data.token) {
          // Accumulate content
          accumulatedContentRef.current += data.token;

          // Update React state directly (remove DOM manipulation)
          chatState.setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botMessageId
                ? {
                    ...msg,
                    content: accumulatedContentRef.current,
                    systemMessage: null,
                  }
                : msg
            )
          );
        }
        break;
      //////////////
        // if (data.token) {
        //   // Accumulate content
        //   accumulatedContentRef.current += data.token;

        //   // Direct DOM update for instant feedback
        //   if (streamingMessageRef.current) {
        //     streamingMessageRef.current.textContent =
        //       accumulatedContentRef.current;
        //   } else {
        //     // Fallback to React state
        //     chatState.setMessages((prev) =>
        //       prev.map((msg) =>
        //         msg.id === botMessageId
        //           ? {
        //               ...msg,
        //               content: accumulatedContentRef.current,
        //               systemMessage: null,
        //             }
        //           : msg
        //       )
        //     );
        //   }
        // }
        // break;

case "final":
  const finalContent = data.content || accumulatedContentRef.current;
  
  // Extract chart configs and data
  let extractedQueryData = data.data;
  let extractedChartConfigs = data.chart_configs || (data.chart_config ? [data.chart_config] : null);
  
  // Keep chart configs WITH their embedded data for rendering
  // Only use the top-level data.data if chart configs don't have embedded data
  if (extractedChartConfigs && extractedChartConfigs.length > 0) {
    const hasEmbeddedData = extractedChartConfigs.some(config => config.data);
    
    // If charts don't have embedded data but we have top-level data, assign it
    if (!hasEmbeddedData && extractedQueryData) {
      // For backward compatibility: assign same data to all charts
      extractedChartConfigs = extractedChartConfigs.map(config => ({
        ...config,
        data: extractedQueryData
      }));
    }
    // If charts have embedded data, keep them as-is (don't extract)
  }
  
  chatState.setMessages((prev) =>
    prev.map((msg) =>
      msg.id === botMessageId
        ? {
            ...msg,
            content: finalContent,
            isStreaming: false,
            systemMessage: null,
            queryData: extractedQueryData,
            chartConfig: extractedChartConfigs?.[0], // First chart with its data
            chartConfigs: extractedChartConfigs, // All charts with their data
            responseType: data.response_type,
            sqlQueries: data.sql_queries,
            recordCount: data.record_count,
            intent: data.intent,
            isMultiStep: data.is_multi_step,
            totalSteps: data.total_steps,
            urls: data.urls,
            images: data.images,
            search_results: data.search_results,
          }
        : msg
    )
  );
  
  // Update chat state
  chatState.setIsTyping(false);
  chatState.setConnectionStatus("connected");
  
  // Update individual state values
  if (extractedQueryData) chatState.setQueryData(extractedQueryData);
  if (extractedChartConfigs && extractedChartConfigs.length > 0) {
    chatState.setChartConfig(extractedChartConfigs[0]);
    if (typeof chatState.setChartConfigs === 'function') {
      chatState.setChartConfigs(extractedChartConfigs);
    }
  }
  if (data.response_type) chatState.setResponseType(data.response_type);
  if (data.sql_queries) chatState.setSqlQueries(data.sql_queries);
  if (data.record_count !== undefined) chatState.setRecordCount(data.record_count);

  // Update session title and reload history
  updateSessionTitle(currentMessageRef.current);
  setTimeout(() => chatHistory.loadChatHistory(true), 1000);
  break;

      case "end":
        chatState.setIsTyping(false);
        chatState.setConnectionStatus("connected");
        break;

      case "error":
        chatState.setIsTyping(false);
        chatState.setConnectionStatus("error");
        setApiError(data.content || data.message || "An error occurred");

        // Update the bot message to show error
        chatState.setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? {
                  ...msg,
                  content: `I apologize, but I encountered an error: ${
                    data.content || data.message
                  }. Please try again.`,
                  isStreaming: false,
                  isError: true,
                  systemMessage: null,
                }
              : msg
          )
        );
        break;

      default:
        console.log("Unhandled stream data type:", data.type, data);
    }
  };

  // Update session title
  const updateSessionTitle = async (firstMessage) => {
    if (chatState.currentSessionTitle !== "New chat") return;
    const title = generateSessionTitle(firstMessage);
    if (title) {
      chatState.setCurrentSessionTitle(title);
      try {
        await chatAPI.apiCall(`/sessions/${chatState.sessionId}/title`, {
          method: "POST",
          body: JSON.stringify({ title }),
        });
      } catch (error) {
        console.warn("Failed to update session title:", error);
      }
    }
  };

  // Generate session title
  const generateSessionTitle = (lastMessage) => {
    if (!lastMessage) return null;
    if (
      lastMessage.toLowerCase().includes("assistant") ||
      lastMessage.toLowerCase().includes("help")
    ) {
      return null;
    }
    const title = lastMessage
      .replace(/[^\w\s-]/g, "")
      .trim()
      .substring(0, 50);
    return title || null;
  };

  // Key press handler
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Get active filters
  const activeFilters = Object.entries(chatFilters.filters).filter(
    ([key, value]) => value && value.trim() !== ""
  );

const handleLoadChat = async (chatId) => {
  try {
    // Show uniform loading screen instead of chat message
    setApiError(null);
    chatState.setIsTyping(true);
    chatState.setConnectionStatus("loading");
    
    // Set session info immediately
    chatState.setSessionId(chatId);
    
    // Find the chat in history to get the title
    const chatSession = chatHistory.chatHistory.find(
      (chat) => chat.id === chatId
    );
    if (chatSession) {
      chatState.setCurrentSessionTitle(chatSession.name);
    }

    // Clear messages while loading to avoid ugly intermediate state
    chatState.setMessages([]);

    // Load the chat messages using the hook
    const messages = await chatHistory.loadSpecificChatHistory(chatId);

    // Update with loaded messages or show welcome message
    if (messages && messages.length > 0) {
      chatState.setMessages(messages);
    } else {
      chatState.setMessages([{
        id: Date.now(),
        type: "bot",
        content: `Welcome back! This is a continuation of "${chatSession?.name}". How can I help you today?`,
        timestamp: new Date(),
      }]);
    }

    // Clear loading states
    chatState.setIsTyping(false);
    chatState.setConnectionStatus("connected");

    // Close sidebar on mobile
    if (window.innerWidth < 1024) {
      setLeftSidebarOpen(false);
    }
  } catch (error) {
    console.error("Error loading chat:", error);
    setApiError(`Failed to load chat: ${error.message}`);
    
    // Show error message
    chatState.setMessages([{
      id: Date.now(),
      type: "bot",
      content: "Failed to load chat history. Starting a new conversation. How can I help you today?",
      timestamp: new Date(),
      isError: true,
    }]);
    
    // Clear loading states
    chatState.setIsTyping(false);
    chatState.setConnectionStatus("error");
  }
};

  return (
    <div className="flex h-full bg-white overflow-hidden">
      {/* Mobile Backdrop */}
      {(leftSidebarOpen || rightSidebarOpen) && window.innerWidth < 1024 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => {
            setLeftSidebarOpen(false);
            setRightSidebarOpen(false);
          }}
        />
      )}

      <ChatSidebar
        isOpen={leftSidebarOpen}
        onClose={() => setLeftSidebarOpen(false)}
        searchQuery={chatHistory.searchQuery}
        onSearchChange={chatHistory.setSearchQuery}
        chatHistory={chatHistory.chatHistory}
        filteredHistory={chatHistory.filteredHistory}
        loadingHistory={chatHistory.loadingHistory}
        refreshingHistory={chatHistory.refreshingHistory}
        sessionId={chatState.sessionId}
        onNewChat={() => {
          chatState.initializeChat();
          chatFilters.clearFilters();
          setInputMessage("");
          setApiError(null);
          if (window.innerWidth < 1024) {
            setLeftSidebarOpen(false);
          }
        }}
        onLoadChat={handleLoadChat}
        onDeleteChat={handleDeleteChat}
        onRefreshHistory={() => chatHistory.loadChatHistory(true)}
        hasMoreChats={chatHistory.hasMoreChats}
        loadMoreChats={chatHistory.loadMoreChats}
      />

      {/* Main Chat Container */}
      <div className="flex-1 flex flex-col min-w-0 bg-white overflow-hidden">
        {/* Chat Header */}
        <ChatHeader
          onToggleLeftSidebar={() => setLeftSidebarOpen(!leftSidebarOpen)}
          onToggleRightSidebar={() => setRightSidebarOpen(!rightSidebarOpen)}
          currentSessionTitle={chatState.currentSessionTitle}
          sessionId={chatState.sessionId}
          connectionStatusText={chatState.getConnectionStatusText()}
          apiError={apiError}
          activeFiltersCount={activeFilters.length}
        />

        {showWelcome ? (
          <div className="flex-1 bg-gray-50 flex items-center justify-center overflow-hidden py-4">
             <WelcomeScreen
               currentContext={chatState.currentContext}
               sampleQuestions={sampleQuestions}
               inputMessage={inputMessage}
               onInputChange={setInputMessage}
               onSubmit={() => handleSendMessage()}
               onQuestionSelect={(question) => {
                 setInputMessage("");
                 handleSendMessage(question);
               }}
               isTyping={chatState.isTyping}
               displayName={displayName}
             />
           </div>
         ) : (
           <>
             <div className="flex-1 overflow-y-auto bg-gray-50">
               <ChatMessages
                 messages={chatState.messages}
                 isTyping={chatState.isTyping}
                 connectionStatus={chatState.connectionStatus}
                 messagesEndRef={messagesEndRef}
                 leftSidebarOpen={leftSidebarOpen}
                 rightSidebarOpen={rightSidebarOpen}
               />
             </div>
             <ChatInput
               inputMessage={inputMessage}
               onInputChange={setInputMessage}
               onSendMessage={handleSendMessage}
               onKeyPress={handleKeyPress}
               isTyping={chatState.isTyping}
               connectionStatus={chatState.connectionStatus}
               apiError={apiError}
               onClearError={() => setApiError(null)}
               activeFilters={activeFilters}
               onClearFilters={chatFilters.clearFilters}
               onFilterChange={chatFilters.handleFilterChange}
             />
           </>
         )}
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={hideConfirm}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        variant={dialogState.variant}
      />

      {/* Right Sidebar - Filters */}
      <ChatFilters
        isOpen={rightSidebarOpen}
        onClose={() => setRightSidebarOpen(false)}
        filters={chatFilters.filters}
        onFilterChange={chatFilters.handleFilterChange}
        onClearFilters={chatFilters.clearFilters}
        dropdownOptions={chatFilters.dropdownOptions}
        loadingDropdowns={chatFilters.loadingDropdowns}
        activeFilters={activeFilters}
        sessionId={chatState.sessionId}
        messages={chatState.messages}
        connectionStatus={chatState.connectionStatus}
        recordCount={chatState.recordCount}
        getConnectionStatusColor={chatState.getConnectionStatusColor}
        getConnectionStatusText={chatState.getConnectionStatusText}
      />
    </div>
  );
};

export default ChatPage;